import { BaseModule, EventBus } from '@mc/tangram-core'
import tracerVuePlugin from '@mc/tracer-vue-plugin'
import { API } from '@/api'
import { companyId } from '@/defines/user'

export const enumCouponType = {
  '1xN': '1N',
  '2xN': '2N',
  scroll: 'Scroll',
  slide: 'Slide'
}

export const enumSingleLineCount = {
  '1xN': 1,
  '2xN': 2,
  scroll: Infinity,
  slide: Infinity
}

export class Coupons extends BaseModule {
  id = ''
  name = 'coupons'
  events = {
    receiveCoupon: 'receive-coupon',
    refresh: 'refresh'
  }

  EventBus = new EventBus()

  constructor(options) {
    super(options)
    const shouldBuildModule = options.data.some(
      group => group.couponInfos && group.couponInfos.length
    )
    if (shouldBuildModule) {
      this.id = options.id
      this.layoutType = options.layoutType
      this.couponType = enumCouponType[this.layoutType] || enumCouponType['1xN']
      this.data = options.data.map((group, index) => ({
        actionId: group.actionId,
        percentage: group.percentage,
        stockstatus: group.stockstatus,
        couponInfos: group.couponInfos.map((coupon, index) => {
          const couponLength = group?.couponInfos?.length || 0
          const singleLineCount =
            enumSingleLineCount[this.layoutType] || enumSingleLineCount['1xN']
          // 处理优惠券内部样式
          this.contentStyle = this.handleContentStyle(index, couponLength)
          // 处理优惠券个数兼容展示（例如：2*N有奇数个时，最下面展示1*N）
          const { couponType, showType = 'normal' } = this.handleCouponType({
            lineCount: singleLineCount,
            parentType: this.couponType,
            index,
            couponLength
          })

          return {
            id: coupon.id,
            couponType,
            showType,
            faceValue: coupon.faceValue,
            name: coupon.couponDisplayName,
            thresholdMsg: coupon.threshold_msg,
            canGet: group.stockstatus === 0,
            tickerInfo: coupon.tickerInfo,
            contentStyle: this.contentStyle,
            actionStatus: coupon?.actionStatus,
            jumpUrl: coupon?.jumpUrl
          }
        }),
        couponPackStyle:
          this.couponType === 'Slide'
            ? {}
            : index === options.data.length - 1
            ? {}
            : this.formatStyle({
                marginBottom: this.valueCalc(this.elementMargin)
              })
      }))
      this.EventBus.on(this.events.receiveCoupon, data => {
        this.receiveCoupon(data)
      })
      this.EventBus.on(this.events.refresh, data => {
        this.refresh(data)
      })
    } else {
      // 如果因为某些条件想中断渲染这个模块，返回空对象即可。
      return {}
    }
  }

  // 处理边距问题
  handleContentStyle(index, couponLength) {
    if (!couponLength) return {}

    const singleLineCount =
      enumSingleLineCount[this.layoutType] || enumSingleLineCount['1xN']

    // 滚动优惠券
    if (singleLineCount === Infinity)
      return this.formatStyle({
        marginRight: this.valueCalc(
          index === couponLength - 1 ? 0 : this.elementMargin
        )
      })

    // 非滚动优惠券
    const lineNum = Math.ceil(couponLength / singleLineCount)
    const nonHandleCouponBoundary =
      couponLength - (couponLength % singleLineCount) // 除去最后一行的边界值（优惠券索引）
    const restCouponsLength = couponLength % singleLineCount // 最后一行的优惠券个数

    const isLastLine = Math.ceil((index + 1) / singleLineCount) === lineNum
    const isLastColumn =
      (index + 1) % singleLineCount === 0 || index === couponLength - 1

    // 优惠券总宽度（减去中间间距）
    const couponsWidth =
      this.contentWidth -
      4 -
      this.elementMargin *
        ((isLastLine && !!restCouponsLength
          ? restCouponsLength
          : singleLineCount) -
          1)

    const contentStyle = this.formatStyle({
      marginRight: this.valueCalc(isLastColumn ? 0 : this.elementMargin),
      marginBottom: this.valueCalc(isLastLine ? 0 : this.elementMargin),
      width:
        index + 1 > nonHandleCouponBoundary
          ? this.valueCalc(couponsWidth / restCouponsLength)
          : this.valueCalc(couponsWidth / singleLineCount)
    })

    return contentStyle
  }

  // 处理优惠券个数兼容方式
  handleCouponType({ lineCount, parentType, index, couponLength }) {
    if (parentType === enumCouponType.scroll)
      return { couponType: enumCouponType.scroll }

    const nonHandleCouponBoundary = couponLength - (couponLength % lineCount)
    if (index + 1 <= nonHandleCouponBoundary) {
      // 边界内（无需处理）
      return { couponType: parentType, showType: 'normal' }
    } else if (parentType === enumCouponType.slide) {
      return { couponType: enumCouponType.slide }
    } else {
      // 边界外
      const restCouponsLength = couponLength - nonHandleCouponBoundary

      const [originLayoutType] = Object.entries(enumSingleLineCount).find(
        item => item[1] === restCouponsLength
      )
      return {
        couponType: enumCouponType[originLayoutType || '1xN'],
        showType: 'simple'
      }
    }
  }

  async receiveCoupon({ groupActionId, couponId }) {
    try {
      const res = await API.coupon.receiveCoupon({
        action_id: groupActionId,
        company_id: companyId
      })
      this.postMessage(this.events.receiveCoupon, {
        ret: 1,
        res
      })
      const group = this.data.find(group => group.actionId === groupActionId)
      const coupon = group.couponInfos.find(coupon => coupon.id === couponId)
      const { tickerInfo } = coupon
      if (tickerInfo?.click) {
        tracerVuePlugin.click(
          tickerInfo.click.spm,
          Object.assign({}, tickerInfo.click.spmJson, tickerInfo.data)
        )
      }
    } catch (error) {
      this.postMessage(this.events.receiveCoupon, {
        ret: 0,
        error
      })
    }
  }

  refresh(data) {
    this.globalEventBus.emit(this.globalEvents.UPDATE_MODULE, {
      moduleId: this.id,
      data
    })
  }

  render() {
    const {
      id,
      pageId,
      couponType,
      containerStyle,
      elementMargin,
      data,
      events,
      globalEvents
    } = this
    return {
      id,
      pageId,
      couponType,
      renderIdentifier: 'coupons',
      containerStyle,
      data,
      elementMargin,
      events,
      globalEvents
    }
  }
}
